import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Space,
  Switch,
  Tag,
  Typography,
  Modal,
  message,
  Select,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import TableWithExport from "../../../generic components/TableWithExport/TableWithExport";
import AdminBlogModal from "./EditBlogModal";

const { Text } = Typography;
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const orderKey = (o) =>
  o == null || Number(o) <= 0 ? Number.POSITIVE_INFINITY : Number(o);

export const BlogsAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [publishedFilter, setPublishedFilter] = useState("all"); // all|true|false|pending
  const [modalOpen, setModalOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(routes.getAdminBlogs, {
        params: { published: publishedFilter },
      });
      const list = data?.blogs || [];
      list.sort(
        (a, b) =>
          orderKey(a.order) - orderKey(b.order) ||
          new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      setRows(list);
    } catch {
      message.error("Failed to load blogs");
    } finally {
      setLoading(false);
    }
  }, [publishedFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const approve = useCallback(async (record, v) => {
    try {
      await axios.patch(`${routes.getAdminBlogs}/${record._id}/publish`, {
        publish: v,
      });
      setRows((prev) =>
        prev.map((r) => (r._id === record._id ? { ...r, isPublished: v } : r))
      );
    } catch {
      message.error("Failed to update publish state");
    }
  }, []);

  const bulkDelete = useCallback(async (selectedRows) => {
    const ids = (selectedRows || [])
      .map((row) => row?._id)
      .filter(Boolean);
    if (!ids.length) return;

    try {
      const { data } = await axios.post(
        `${routes.getAdminBlogs}/bulk-delete`,
        { ids }
      );
      message.success(
        data?.message ||
          `Deleted ${ids.length} blog${ids.length === 1 ? "" : "s"}`
      );
      await fetchList();
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to delete selected blogs"
      );
    }
  }, [fetchList]);

  const remove = useCallback(
    (record) => {
      Modal.confirm({
        title: `Delete "${record.title}"?`,
        okType: "danger",
        okButtonProps: { danger: true },
        onOk: () => bulkDelete([record]),
      });
    },
    [bulkDelete]
  );
  const openModal = useCallback((record) => {
    setCurrent(record);
    setModalOpen(true);
  }, []);
  const onSaved = (updated) => {
    if (!updated) return;
    // update the row in-place (keep your sort if you have manual order)
    setRows((prev) =>
      prev
        .map((r) => (r._id === updated._id ? { ...r, ...updated } : r))
        .sort(
          (a, b) =>
            (a.order ?? Number.POSITIVE_INFINITY) -
              (b.order ?? Number.POSITIVE_INFINITY) ||
            new Date(b.created_at || b.createdAt || 0) -
              new Date(a.created_at || a.createdAt || 0)
        )
    );
  };

  const columns = useMemo(
    () => [
      { title: "#", width: 64, align: "center", render: (_v, _r, i) => i + 1 },
      {
        title: "Title",
        dataIndex: "title",
        sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
        render: (v) => (
          <Text strong ellipsis={{ tooltip: v }}>
            {v || "—"}
          </Text>
        ),
      },
      {
        title: "Author",
        render: (_, r) => r?.author?.fullName || r?.author?.email || "—",
      },
      {
        title: "Published",
        width: 180,
        render: (_, r) => (
          <Space>
            <Switch
              checked={r.isPublished === true}
              indeterminate={r.isPublished === undefined}
              onChange={(c) => approve(r, c)}
            />
            {r.isPublished === true ? (
              <Tag color="green">Approved</Tag>
            ) : r.isPublished === false ? (
              <Tag color="red">Declined</Tag>
            ) : (
              <Tag color="gold">Pending</Tag>
            )}
          </Space>
        ),
      },
      {
        title: "Order",
        dataIndex: "order",
        width: 90,
        sorter: (a, b) =>
          orderKey(a.order) - orderKey(b.order) ||
          new Date(b.created_at || 0) - new Date(a.created_at || 0),
        render: (v) => (v > 0 ? v : "—"),
      },
      {
        title: "Views",
        dataIndex: "views",
        width: 90,
        sorter: (a, b) =>
          orderKey(a.views) - orderKey(b.views) ||
          new Date(b.created_at || 0) - new Date(a.created_at || 0),
        render: (v) => (v > 0 ? v : "—"),
      },
      {
        title: "Created",
        width: 200,
        sorter: (a, b) =>
          new Date(a.created_at || 0) - new Date(b.created_at || 0),
        render: (_, r) => fmtDate(r.created_at),
      },
      {
        title: "Actions",
        width: 180,
        fixed: "right",
        render: (_, record) => (
          <Space>
            {/* wire to your edit modal if needed */}
            <Button icon={<EditOutlined />} onClick={() => openModal(record)} />
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => remove(record)}
            />
          </Space>
        ),
      },
    ],
    [approve, openModal, remove]
  );

  return (
    <div>
      <h2 className="adminTitle" style={{ marginBottom: 16 }}>
        Blogs
      </h2>

      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          value={publishedFilter}
          onChange={setPublishedFilter}
          options={[
            { label: "All", value: "all" },
            { label: "Approved", value: "true" },
            { label: "Declined", value: "false" },
            { label: "Pending", value: "pending" },
          ]}
          style={{ width: 180 }}
        />
      </Space>

      <TableWithExport
        columns={columns}
        data={rows}
        loading={loading}
        tableName="blogs"
        enableReorder
        getRowKey={(r) => r._id}
        onReorder={async (ids) => {
          await axios.post(routes.getAdminBlogs + "/reorder", { ids });
          await fetchList();
        }}
        selectable
        onBulkDelete={(selected) => {
          if (!selected?.length) return;
          const many = selected.length > 1;
          Modal.confirm({
            title: many
              ? `Delete ${selected.length} blogs?`
              : `Delete "${selected[0]?.title || "this blog"}"?`,
            okText: "Delete",
            okType: "danger",
            okButtonProps: { danger: true },
            onOk: () => bulkDelete(selected),
          });
        }}
      />

      <AdminBlogModal
        open={modalOpen}
        blog={current}
        onClose={() => setModalOpen(false)}
        onSaved={onSaved}
      />
    </div>
  );
};
export default BlogsAdmin;
